import { useEffect, useMemo, useState } from 'react';
import {
  ContainerAppFeature,
  ContainerBillingChangePreview,
  ContainerFeature,
  Plan,
  User,
} from '@back4app2/sdk';
import back4app2 from '../back4app2';
import { DsButton, Eyebrow, ModalDialog, useSettingsSave } from '../components/ds';
import useContainerPurchase from '../hooks/useContainerPurchase';
import { formatMoney } from './PlanAndExtras';

/** How the app is billed, which decides what the button does. */
export type PlanBillingMode = 'SUBSCRIPTION' | 'CHECKOUT';

/**
 * The extras an app holds, and the button that makes a plan and packs real.
 *
 * Settings' plan table picks the plan (`draftPlan`); this owns the pack counts,
 * the new monthly total and the one action:
 *
 *   SUBSCRIPTION  the app has its own subscription. The button opens a
 *                 confirmation with what Paddle will charge now, prorated, and
 *                 confirming replaces the subscription's lines in one update.
 *   CHECKOUT      a Free app, or a paid one whose legacy subscription could
 *                 not be adopted (`replacesPreviousPlan`). The button opens a
 *                 checkout with the plan and every pack as lines with a
 *                 quantity; for the latter it is the full price, and the old
 *                 subscription is cancelled by the team.
 *
 * Two rules come from the server and are mirrored here only so the controls do
 * not offer what will be refused: a pack can be ADDED only where the extra is
 * sold on the chosen plan, and the last database pack cannot be removed yet.
 */
const PlanPacksEditor = ({
  appId,
  user,
  mode,
  replacesPreviousPlan = false,
  currentPlan,
  draftPlan,
  owned,
  wantFeatureId,
  blockedReason,
  liftsCancellation = false,
  onChanged,
  onDiscard,
}: {
  appId: string;
  user?: User;
  mode: PlanBillingMode;
  /** A paid app starting a new subscription in place of its legacy one. */
  replacesPreviousPlan?: boolean;
  currentPlan?: Plan;
  /** The plan picked in the table. Undefined until a paid one is picked. */
  draftPlan?: Plan;
  /** What the app holds today. */
  owned: ContainerAppFeature[];
  /**
   * An extra the reader arrived intending to buy, already resolved from the
   * `?add=<kind>` the offer screens link with. One pack goes into the basket;
   * they can still take it out.
   */
  wantFeatureId?: string;
  /** Why nothing can change right now, in one sentence. */
  blockedReason?: string;
  /**
   * The app is scheduled to drop to Free, and paying for a plan here calls that
   * off — one change, decided and applied on the server. Said out loud before
   * the card is charged, because it is the half of the purchase the buyer did
   * not explicitly ask for.
   */
  liftsCancellation?: boolean;
  onChanged: () => void;
  /** Put the plan picked in the table back to the current one. */
  onDiscard: () => void;
}) => {
  // Lazy: Settings runs the legacy checkout's Paddle too. Plain overlay: no
  // glass on this page.
  const { purchase, checkoutModal } = useContainerPurchase({
    lazy: true,
    plainOverlay: true,
  });
  const [catalog, setCatalog] = useState<ContainerFeature[] | undefined>();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<ContainerBillingChangePreview>();
  const [isReviewing, setIsReviewing] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  /**
   * The money went through. Set instead of leaving the screen straight away:
   * both paths end somewhere the buyer cannot see what they bought — Paddle's
   * own frame closes on nothing, and a subscription change closes a dialog —
   * so this says it landed before handing them to the Overview, which is where
   * the deploy it started can actually be watched.
   *
   * `charged` separates the two: a checkout and an upgrade take money now, a
   * downgrade that comes out as a credit does not, and calling that a payment
   * would be a lie on the one screen where money is the subject.
   */
  const [paid, setPaid] = useState<{ charged: boolean } | null>(null);

  const ownedCounts = useMemo(() => {
    const result: Record<string, number> = {};

    for (const row of owned) {
      if (row.status === 'ACTIVE') {
        result[row.featureId] = (result[row.featureId] || 0) + row.quantity;
      }
    }

    return result;
  }, [owned]);

  useEffect(() => {
    setCounts(wantFeatureId
      ? { ...ownedCounts, [wantFeatureId]: Math.max(1, ownedCounts[wantFeatureId] || 0) }
      : ownedCounts);
  }, [ownedCounts, wantFeatureId]);

  useEffect(() => {
    let canceled = false;

    back4app2
      .containerFeatures()
      .then((rows) => {
        if (!canceled) {
          setCatalog(rows);
        }
      })
      .catch((e) => {
        console.error('could not load the extras catalog', e);

        if (!canceled) {
          setCatalog([]);
        }
      });

    return () => { canceled = true; };
  }, []);

  // Everything the app holds, plus everything it could buy. An extra the app
  // already has stays listed on any plan — keeping is never gated.
  const rows = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; price: number; kind?: string; maxStorageBytes?: number | null; planIds: string[] }>();

    for (const feature of catalog || []) {
      byId.set(feature.id, feature);
    }

    for (const row of owned) {
      if (!byId.has(row.featureId)) {
        byId.set(row.featureId, {
          id: row.featureId,
          name: row.name,
          price: row.price,
          // Not in the catalog any more (disabled, or repriced away). An
          // engine means it is a database, which is what the floor rule needs.
          kind: row.engine ? 'database' : undefined,
          maxStorageBytes: row.maxStorageBytes,
          planIds: [],
        });
      }
    }

    return Array.from(byId.values());
  }, [catalog, owned]);

  const extras = rows
    .map((row) => ({ featureId: row.id, quantity: counts[row.id] || 0 }))
    .filter((extra) => extra.quantity > 0);

  const total =
    (draftPlan?.price || 0) +
    rows.reduce((sum, row) => sum + row.price * (counts[row.id] || 0), 0);

  const isPlanChange = Boolean(draftPlan && draftPlan.id !== currentPlan?.id);
  const isPacksChange = rows.some(
    (row) => (counts[row.id] || 0) !== (ownedCounts[row.id] || 0)
  );
  // A Free app buys by picking a plan. A paid one — replacing its legacy
  // subscription included — needs something to actually change.
  const hasChange = mode === 'CHECKOUT' && !replacesPreviousPlan
    ? Boolean(draftPlan)
    : isPlanChange || isPacksChange;

  const setCount = (featureId: string, next: number) =>
    setCounts((current) => ({ ...current, [featureId]: Math.max(0, next) }));

  const onReview = async () => {
    if (!draftPlan) {
      return;
    }

    setErrorMessage('');

    if (mode === 'CHECKOUT') {
      if (!user) {
        return;
      }

      setIsWorking(true);

      const outcome = await purchase({
        appId,
        planId: draftPlan.id,
        extras,
        user,
        summary: {
          eyebrow: replacesPreviousPlan ? 'new subscription' : `upgrade to ${draftPlan.name}`,
          note: replacesPreviousPlan ? 'Your previous plan will be canceled.' : undefined,
          planName: draftPlan.name,
          planPrice: draftPlan.price,
          lines: extras.map((extra) => {
            const row = rows.find((candidate) => candidate.id === extra.featureId);

            return {
              id: extra.featureId,
              name: row?.name || 'Extra',
              detail: extra.quantity > 1 ? `× ${extra.quantity}` : undefined,
              price: (row?.price || 0) * extra.quantity,
            };
          }),
        },
      });

      setIsWorking(false);

      if (outcome.ok) {
        // A checkout is always money taken now.
        setPaid({ charged: true });
      } else if (outcome.reason === 'FAILED') {
        setErrorMessage(outcome.message || "We couldn't complete the payment.");
      }

      return;
    }

    setIsWorking(true);

    try {
      setPreview(
        await back4app2.containerBillingChangePreview(appId, draftPlan.id, extras)
      );
      setIsReviewing(true);
    } catch (e) {
      console.error('could not preview the change', e);
      setErrorMessage(
        e instanceof Error && e.message
          ? e.message
          : "We couldn't work out the price of this change. Try again in a minute."
      );
    } finally {
      setIsWorking(false);
    }
  };

  const onConfirm = async () => {
    if (!draftPlan) {
      return;
    }

    setIsWorking(true);
    setErrorMessage('');

    try {
      await back4app2.updateContainerAppBilling(appId, draftPlan.id, extras);
      setIsReviewing(false);
      setPaid({ charged: Number(preview?.dueNow) > 0 });
    } catch (e) {
      console.error('could not change the plan', e);
      setIsReviewing(false);
      setErrorMessage(
        e instanceof Error && e.message
          ? e.message
          : "We couldn't change this plan. Nothing was charged."
      );
    } finally {
      setIsWorking(false);
    }
  };

  const dueNow = preview
    ? (preview.currencyCode === 'USD' ? '$' : `${preview.currencyCode} `) +
      (Number(preview.dueNow) / 100).toFixed(2)
    : '';
  /**
   * A change that takes no money now.
   *
   * Going DOWN a plan is the common one: the period is already paid for, so the
   * proration is a credit rather than a charge and Paddle quotes nothing due.
   * The confirmation was written for upgrades and said it anyway — "$0.00 is
   * charged now", on a button reading "Pay $0.00 now", which reads like a
   * broken price rather than like good news.
   */
  const chargesNow = Boolean(preview) && Number(preview?.dueNow) > 0;
  /**
   * A plan that starts later — a downgrade.
   *
   * The app keeps the size it paid for until the period ends, so this modal has
   * to say a date, not just a price: agreeing to "Starter, $5/month" while the
   * app goes on running Shared for three weeks is only confusing if the screen
   * never said when.
   */
  const planStartsOn = preview?.planStartsAt
    ? new Date(preview.planStartsAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : undefined;

  /** "Starter → Shared 1 GB · PostgreSQL database 1 → 2 packs" */
  const planName = (plan?: Plan) =>
    !plan
      ? 'Free'
      : plan.name === 'Starter' || plan.price === 0
      ? plan.name
      : `${plan.name} ${plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024} GB` : `${plan.maxRAM} MB`}`;
  const changedRows = rows.filter(
    (row) => (counts[row.id] || 0) !== (ownedCounts[row.id] || 0)
  );
  const summary = [
    isPlanChange || (mode === 'CHECKOUT' && draftPlan && !replacesPreviousPlan)
      ? `${planName(currentPlan)} → ${planName(draftPlan)}`
      : undefined,
    ...changedRows.map((row) => {
      const next = counts[row.id] || 0;

      return `${row.name} ${ownedCounts[row.id] || 0} → ${next} ${next === 1 ? 'pack' : 'packs'}`;
    }),
  ].filter(Boolean).join(' · ');

  // What the app would run on with nothing changed, for the bar at rest.
  const restingSummary = [
    planName(draftPlan || currentPlan),
    ...rows
      .filter((row) => (counts[row.id] || 0) > 0)
      .map((row) => `${counts[row.id]} × ${row.name}`),
  ].join(' · ');
  const hasDatabase = rows.some((row) => row.kind === 'database' && (counts[row.id] || 0) > 0);

  // The Settings bar at the bottom of the screen carries the change: what it
  // is, what the month will cost, and the one button. It stays up with nothing
  // changed, because it is also where the price is read — and it is what warns
  // before leaving the tab with a change that was never paid for.
  useSettingsSave({
    dirtyCount: hasChange && draftPlan ? Math.max(1, (isPlanChange ? 1 : 0) + changedRows.length) : 0,
    isSubmitting: isWorking,
    canSave: !blockedReason,
    onSave: onReview,
    onDiscard: () => {
      setCounts(ownedCounts);
      setErrorMessage('');
      onDiscard();
    },
    summary: hasChange && draftPlan ? summary : restingSummary,
    total: formatMoney(total).replace(/\.00$/, ''),
    saveLabel: mode === 'CHECKOUT' ? 'Continue to checkout' : 'Review and pay',
    savingLabel: 'Working…',
    alwaysVisible: true,
    idleLabel: 'No changes yet',
    idleTotalLabel: 'total',
    // Short, and only pointing at something step 2 can actually do: on a plan
    // that does not sell a database the extra is there but cannot be added.
    hint: rows.length === 0
      ? undefined
      : hasDatabase
      ? 'Includes extras — see step 2 ↓'
      : rows.some((row) => row.kind === 'database' && Boolean(draftPlan && row.planIds.includes(draftPlan.id)))
      ? 'Need a database? Add one in step 2 ↓'
      : 'Databases are available on paid plans ↓',
    onHint: () => document.getElementById('plan-extras')?.scrollIntoView({ block: 'start' }),
  });

  return (
    <div id="plan-extras" className="flex flex-col gap-5 scroll-mt-6">
      {checkoutModal}

      <div className="flex flex-col gap-1.5">
        <Eyebrow>step 2 of 2</Eyebrow>
        <h2 className="m-0 font-sora text-[18px] font-semibold tracking-[-0.01em] leading-[1.25] text-white">
          Extras
        </h2>
        <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">
          Billed on top of the plan. A database is required if the app stores data.
        </p>
        {replacesPreviousPlan ? (
          <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">
            Changes start a new subscription at the full monthly price. Your previous plan will be canceled.
          </p>
        ) : null}
        {errorMessage ? (
          <p className="m-0 text-ds-135 leading-[1.5] text-state-err">{errorMessage}</p>
        ) : null}
      </div>

      {catalog === undefined ? (
        <div className="rounded-card border border-navy-500 bg-navy-800 px-6 py-6 text-ds-15 text-navy-300">Loading extras…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-card border border-navy-500 bg-navy-800 px-6 py-6 text-ds-15 text-navy-300">No extras are available.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const count = counts[row.id] || 0;
            const ownedCount = ownedCounts[row.id] || 0;
            const isSoldOnPlan = Boolean(draftPlan && row.planIds.includes(draftPlan.id));
            // Adding past what is owned needs the extra on the chosen plan.
            const canAdd = Boolean(draftPlan) && !blockedReason && (isSoldOnPlan || count < ownedCount);
            // The last database pack stays until the purge exists.
            const floor = row.kind === 'database' && ownedCount > 0 ? 1 : 0;
            const canRemove = !blockedReason && count > floor;

            return (
              <div
                key={row.id}
                className={
                  'rounded-container border px-5 py-[18px] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 transition-colors duration-[120ms] ' +
                  // An extra with packs in it: its edge says so, the way the
                  // selected row in the table is marked.
                  (count > 0 ? 'border-state-ok bg-state-ok/5' : 'border-navy-500 bg-navy-800')
                }
              >
                <div className="min-w-0 flex flex-col gap-1">
                  <span className="text-[15.5px] font-medium text-white">{row.name}</span>
                  <span className="font-courrier text-[length:calc(12px*var(--b4-type,1))] text-navy-300">
                    {[
                      `${formatMoney(row.price).replace(/\.00$/, '')} / pack`,
                      typeof row.maxStorageBytes === 'number'
                        ? `${Math.round(row.maxStorageBytes / 1024 ** 3)} GB each`
                        : undefined,
                      draftPlan && !isSoldOnPlan && count <= ownedCount
                        ? `not sold on ${draftPlan.name}`
                        : undefined,
                    ].filter(Boolean).join(' · ')}
                  </span>
                </div>

                <div className="flex-none flex items-center gap-6">
                  <div className="inline-flex items-center h-[40px] rounded-surface border border-navy-500 overflow-hidden">
                    <button
                      type="button"
                      aria-label={`Remove a ${row.name} pack`}
                      disabled={!canRemove}
                      onClick={() => setCount(row.id, count - 1)}
                      className="b4-focus h-full w-[38px] inline-flex items-center justify-center text-[18px] text-navy-100 hover:bg-navy-700 hover:text-white disabled:text-navy-500 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                    >
                      −
                    </button>
                    <span className="min-w-[44px] text-center font-courrier text-[length:calc(14px*var(--b4-type,1))] text-white" aria-live="polite">
                      {count}
                    </span>
                    <button
                      type="button"
                      aria-label={`Add a ${row.name} pack`}
                      disabled={!canAdd}
                      onClick={() => setCount(row.id, count + 1)}
                      className="b4-focus h-full w-[38px] inline-flex items-center justify-center text-[18px] text-navy-100 hover:bg-navy-700 hover:text-white disabled:text-navy-500 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                  <span className="min-w-[96px] text-right whitespace-nowrap">
                    <span className="font-semibold text-[16px] text-white">
                      {formatMoney(row.price * count).replace(/\.00$/, '')}
                    </span>
                    <span className="text-ds-125 text-navy-300"> / mo</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ModalDialog
        open={isReviewing}
        onClose={() => (isWorking ? undefined : setIsReviewing(false))}
        title="Confirm the change"
        blurb={
          preview
            ? chargesNow
              ? `${dueNow} is charged now to the card on this app's subscription, prorated for the rest of the period.`
              : 'Nothing is charged now — this app has already paid for the rest of the period.'
            : undefined
        }
        footer={
          <>
            <DsButton variant="outline" isDisabled={isWorking} onClick={() => setIsReviewing(false)}>
              Back
            </DsButton>
            <DsButton variant="primary" isDisabled={isWorking} onClick={onConfirm}>
              {isWorking
                ? chargesNow ? 'Charging…' : 'Applying…'
                : chargesNow ? `Pay ${dueNow} now` : 'Confirm the change'}
            </DsButton>
          </>
        }
      >
        {preview ? (
          <div className="flex flex-col gap-1.5 text-ds-135 text-navy-200">
            {/* First, because it is the part of this they did not click for:
                the app was on its way to Free and this purchase stops it. */}
            {liftsCancellation ? (
              <span className="text-white">
                The scheduled cancellation is called off — the app stays on a paid plan.
              </span>
            ) : null}
            {planStartsOn ? (
              <span className="text-white">
                {planName(draftPlan)} starts on {planStartsOn}. Until then the app keeps
                running {planName(currentPlan)}, which is already paid for.
              </span>
            ) : null}
            <span>
              {planStartsOn ? 'From that date' : 'From then on'}:{' '}
              <span className="text-white">{formatMoney(preview.monthlyTotal)} / month</span>
            </span>
            {preview.redeploys ? (
              <span>The app redeploys to run on the new plan's CPU and memory.</span>
            ) : null}
          </div>
        ) : null}
      </ModalDialog>

      {/* The last thing this screen has to say. Clicking away leaves the same
          way the button does: there is nothing left to do here, and stopping
          on a stale plan table is how someone pays twice. */}
      <ModalDialog
        open={!!paid}
        onClose={onChanged}
        title={paid?.charged ? 'Payment received' : 'Plan updated'}
        blurb={
          planStartsOn
            ? `${planName(draftPlan)} starts on ${planStartsOn}. Nothing changes about the app today — it keeps running ${planName(currentPlan)}, which is paid for.`
            : 'Your app is redeploying on the new plan. You can follow it on the Overview.'
        }
        footer={
          <DsButton variant="primary" onClick={onChanged}>
            Go to the Overview
          </DsButton>
        }
      />
    </div>
  );
};

export default PlanPacksEditor;
